import { FC, useEffect, useState } from "react";
import { Table, Input, Button, notification, Modal } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import style from "./style.module.scss";
import API from "../../../services/api.service";
import { APIS } from "../../../constants/apis";

const { TextArea } = Input;

const SettingFaq: FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [question, setQuestion] = useState<string>("");
  const [answer, setAnswer] = useState<string>("");
  const [addOpen, setAddOpen] = useState(false);
  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState<string>("");
  const [editAnswer, setEditAnswer] = useState<string>("");
  const [editSaving, setEditSaving] = useState(false);

  // The app and the admin panel read the same booking/faq endpoint. Each item is
  // { id, sr, description: [question, answer] } — id is present for DB-backed rows
  // and absent for the built-in fallback list (shown only if the table is empty).
  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await API.get(APIS.FAQ.LIST);
      if (res.data.status === 1) {
        const faqs = (res.data.data?.FAQ || []).map((f: any, idx: number) => ({
          id: f.id,
          // Display serial = position in the list, so it always reads 1,2,3…
          // and renumbers after an add/delete instead of leaving gaps.
          sr: idx + 1,
          question: f.description?.[0] || "",
          answer: f.description?.[1] || "",
        }));
        setList(faqs);
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const onAdd = async () => {
    if (!question.trim() || !answer.trim()) return; // Add button is disabled anyway
    try {
      setSaving(true);
      const res = await API.post(APIS.FAQ.ADD, {
        question: question.trim(),
        answer: answer.trim(),
      });
      if (res.data.status === 1) {
        notification.success({
          message: res.data.data?.message || "FAQ added",
          placement: "top",
        });
        setQuestion("");
        setAnswer("");
        setAddOpen(false);
        fetchList();
      } else {
        notification.error({
          message: res.data.data?.message || res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message:
          e?.response?.data?.data?.message ||
          e?.response?.data?.message ||
          "Something went wrong",
        placement: "top",
      });
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setQuestion("");
    setAnswer("");
    setAddOpen(true);
  };

  // Edit now opens a dialog instead of populating the top card.
  const onEdit = (row: any) => {
    setEditId(row.id);
    setEditQuestion(row.question);
    setEditAnswer(row.answer);
    setEditOpen(true);
  };

  const onUpdate = async () => {
    if (!editQuestion.trim() || !editAnswer.trim() || !editId) return;
    try {
      setEditSaving(true);
      const res = await API.put(APIS.FAQ.UPDATE + editId, {
        question: editQuestion.trim(),
        answer: editAnswer.trim(),
      });
      if (res.data.status === 1) {
        notification.success({
          message: res.data.data?.message || "FAQ updated",
          placement: "top",
        });
        setEditOpen(false);
        fetchList();
      } else {
        notification.error({
          message: res.data.data?.message || res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message:
          e?.response?.data?.data?.message ||
          e?.response?.data?.message ||
          "Something went wrong",
        placement: "top",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const onDelete = async (row: any) => {
    try {
      const res = await API.delete(APIS.FAQ.DELETE + row.id);
      if (res.data.status === 1) {
        notification.success({
          message: res.data.data?.message || "FAQ deleted",
          placement: "top",
        });
        fetchList();
      } else {
        notification.error({
          message: res.data.data?.message || "Failed",
          placement: "top",
        });
      }
    } catch (e) {
      notification.error({ message: "Something went wrong", placement: "top" });
    }
  };

  // centered confirm popup for delete
  const confirmDelete = (row: any) => {
    Modal.confirm({
      title: "Are you sure you want to delete this FAQ?",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => onDelete(row),
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "sr",
      key: "sr",
      width: 60,
      render: (sr: any) => <span className={style.sr_cell}>{sr}</span>,
    },
    {
      title: "Question",
      dataIndex: "question",
      key: "question",
      width: 320,
      render: (q: string) => <span className={style.q_cell}>{q}</span>,
    },
    {
      title: "Answer",
      dataIndex: "answer",
      key: "answer",
      render: (a: string) => <span className={style.a_cell}>{a}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_: any, row: any) =>
        row.id ? (
          <span className={style.row_actions}>
            <EditOutlined className={style.icon} onClick={() => onEdit(row)} />
            <DeleteOutlined
              className={`${style.icon} ${style.danger}`}
              onClick={() => confirmDelete(row)}
            />
          </span>
        ) : (
          <span style={{ color: "#aeb2b7", fontSize: 12 }}>default</span>
        ),
    },
  ];

  const addDisabled = !question.trim() || !answer.trim();
  const editDisabled = !editQuestion.trim() || !editAnswer.trim();

  return (
    <div className={style.faq_wrap}>
      <div className={style.head}>
        <div className={style.ctc}>
          <Button className={style.add_btn} onClick={openAdd}>
          Add FAQ
        </Button>
        </div>
       
      </div>

      {/* table */}
      <div className={style.table_wrap}>
        <Table
          rowKey={(r: any) => (r.id ? `f-${r.id}` : `s-${r.sr}`)}
          columns={columns}
          dataSource={list}
          loading={loading}
          pagination={{
            pageSize: 10,
            // "10 of 20" — items shown up to this page, out of the total.
            showTotal: (total, range) => `${range[1]} of ${total}`,
          }}
          size="middle"
        />
      </div>

      {/* add dialog */}
      <Modal
        title={null}
        footer={null}
        visible={addOpen}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>Add FAQ</div>
        <div className={style.field}>
          <label className={style.label}>Question</label>
          <TextArea
            placeholder="e.g. How many days can I stay in a calendar year?"
            autoSize={{ minRows: 1, maxRows: 3 }}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>
        <div className={style.field}>
          <label className={style.label}>Answer</label>
          <TextArea
            placeholder="Write the answer owners will see in the app…"
            autoSize={{ minRows: 3, maxRows: 8 }}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </div>
        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setAddOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={saving}
            disabled={addDisabled}
            onClick={onAdd}
          >
            Add
          </Button>
        </div>
      </Modal>

      {/* edit dialog */}
      <Modal
        title={null}
        footer={null}
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>Edit FAQ</div>
        <div className={style.field}>
          <label className={style.label}>Question</label>
          <TextArea
            placeholder="e.g. How many days can I stay in a calendar year?"
            autoSize={{ minRows: 1, maxRows: 3 }}
            value={editQuestion}
            onChange={(e) => setEditQuestion(e.target.value)}
          />
        </div>
        <div className={style.field}>
          <label className={style.label}>Answer</label>
          <TextArea
            placeholder="Write the answer owners will see in the app…"
            autoSize={{ minRows: 3, maxRows: 8 }}
            value={editAnswer}
            onChange={(e) => setEditAnswer(e.target.value)}
          />
        </div>
        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={editSaving}
            disabled={editDisabled}
            onClick={onUpdate}
          >
            Update
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SettingFaq;
